import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getAgreementById, updateAgreementStatus } from '../services/agreementService';
import { useAgreement } from '../context/AgreementContext';


const STEP_GUIDANCES: { label: string; children: React.ReactNode }[] = [
  {
    label: 'Onboarding',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <div className="font-medium text-gray-900 mb-2">Next steps to get started</div>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li className="pl-2">Carefully review the proposal and engagement agreement. Once you're ready to proceed, sign the documents and submit the initial retainer to formally begin the process.</li>
          <li className="pl-2">You'll be introduced to your dedicated Account Manager; your main point of contact from onboarding right through to property settlement.</li>
          <li className="pl-2">Schedule an introductory call with your Account Manager to discuss your investment goals, property preferences, and timelines. This helps us tailor our approach to suit your needs from the very start.</li>
        </ul>
        
        <div className="font-medium text-gray-900 mb-2">If you're already pre-approved:</div>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li className="pl-2">Please send a copy of your current pre-approval to your Account Manager.</li>
        </ul>
        
        <div className="font-medium text-gray-900 mb-2">If you're not yet pre-approved:</div>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li className="pl-2">No worries! Your Account Manager can connect you with one of our trusted mortgage brokers to assist you in obtaining pre-approval. Once received, simply forward it to your Account Manager.</li>
        </ul>
        
        <p>A dedicated WhatsApp group, managed by your Account Manager, will be established to streamline communication and provide real-time updates throughout the entire process.</p>
      </div>
    )
  },
  {
    label: 'Property Search',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li className="pl-2">Our Sourcing Team identifies and evaluates high-quality property opportunities tailored to your investment goals, including exclusive off-market and pre-market deals in high-growth areas.</li>
          <li className="pl-2">Each property undergoes strict initial due diligence, with only the top 10% progressing to final review.</li>
          <li className="pl-2">Once a suitable property is identified, your dedicated Account Manager will coordinate a review meeting with you to discuss the details.</li>
        </ul>
        
        <p className="mb-2">Ahead of this discussion, you will receive two comprehensive reports:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li className="pl-2"><strong>Property Deep Dive Report (PDDR)</strong> - A detailed analysis of the property and location.</li>
          <li className="pl-2"><strong>Numbers Report</strong> - A financial overview including cash flow projections and estimated acquisition costs.</li>
        </ul>
      </div>
    )
  },
  {
    label: 'Presentation',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li className="pl-2">Carefully review the provided reports and note any questions ahead of your scheduled call.</li>
          <li className="pl-2">During the call, we will walk you through the property in detail and address all queries to ensure you feel fully informed and confident in your decision.</li>
        </ul>
        
        <p className="mb-2">Once you provide verbal approval, we will:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li className="pl-2">Initiate contract preparation with the sales agent.</li>
          <li className="pl-2">Introduce you to our recommended conveyancer.</li>
        </ul>
      </div>
    )
  },
  {
    label: 'Exchange',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <ul className="list-disc pl-6 space-y-2">
          <li className="pl-2">Following the conveyancer's review, you will be asked to review and sign the contract.</li>
          <li className="pl-2">Once signed, the sales agent or conveyancer will provide instructions to pay the holding deposit and secure the property.</li>
        </ul>
      </div>
    )
  },
  {
    label: 'Pre-Settlement',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <p className="mb-2">We support you through the final stages of due diligence to ensure a smooth path to settlement:</p>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li className="pl-2">Coordinate a video walkthrough of the property for your review.</li>
          <li className="pl-2">Work closely with your mortgage broker to secure unconditional finance approval.</li>
          <li className="pl-2">Arrange introductions to trusted professionals, including a property manager, pest and building inspectors.</li>
          <li className="pl-2">Review all inspection reports with you and manage any additional negotiations, if required.</li>
        </ul>
        
        <p>Once all contract conditions are satisfied, the contract becomes unconditional. At this stage, we also schedule your final pre-settlement inspection.</p>
      </div>
    )
  },
  {
    label: 'Settlement',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <ul className="list-disc pl-6 space-y-2">
          <li className="pl-2">We manage communication with all relevant parties to ensure a seamless settlement process.</li>
          <li className="pl-2">Once settlement is complete, we will confirm finalisation with you and guide you through any remaining steps.</li>
        </ul>
      </div>
    )
  },
  {
    label: '6-Month Strategy Check-in',
    children: (
      <div className="text-gray-700 leading-relaxed">
        <p>Six months after settlement, we will reconnect to assess your property's performance and explore your next acquisition, ensuring it aligns with your long-term investment strategy.</p>
      </div>
    )
  }
];

export function WelcomePage() {
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const navigate = useNavigate();
  const { dispatch } = useAgreement();
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [viewedSteps, setViewedSteps] = useState<Set<number>>(new Set());
  const [agreement, setAgreement] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const location = useLocation();
  
  // Check if user is coming from the "Back to Review" button using location state
  const isFromBackButton = location.state && (location.state as any).fromBackButton === true;
  
  // Helper function to calculate amount with GST
  const calculateWithGST = (amount: number | undefined): string => {
    if (!amount) return "N/A";
    const withGST = amount * 1.1; // Add 10% GST
    return `$${withGST.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  useEffect(() => {
    const fetchAgreement = async () => {
      if (!agreement_id) return;
      
      try {
        setLoading(true);
        const data = await getAgreementById(agreement_id);
        setAgreement(data);
        setError(null);
        
        // Load agreement data into context
        if (data.purchaser_details) {
          // Load purchaser type into context
          if (data.purchaser_details.purchaserType) {
            dispatch({
              type: 'SET_PURCHASER_TYPE',
              payload: data.purchaser_details.purchaserType
            });
            console.log("Loaded purchaser type into context:", data.purchaser_details.purchaserType);
          }
          
          // Load preferences if available
          if (data.purchaser_details.preferences) {
            dispatch({
              type: 'SET_PREFERENCES',
              payload: data.purchaser_details.preferences
            });
            console.log("Loaded preferences into context");
          }
          
          // Load individual details if available
          if (data.purchaser_details.individualDetails) {
            dispatch({
              type: 'SET_INDIVIDUAL_DETAILS',
              payload: data.purchaser_details.individualDetails
            });
            console.log("Loaded individual details into context");
          }
        }
        
        // Auto-redirect logic based on agreement status
        if (!isFromBackButton) {
          if (data.status === "signed") {
            // If agreement is signed, redirect to start-agreement page
            console.log("Agreement is signed, redirecting to start-agreement page");
            navigate(`/${agreement_id}/start-agreement`);
          } else if (data.status === "confirmed") {
            // If agreement is confirmed, redirect to payment page
            console.log("Agreement is confirmed, redirecting to payment page");
            navigate(`/${agreement_id}/payment`);
          } else if (data.status === "reviewed") {
            // Check if purchaser type has been set
            if (data.purchaser_details && data.purchaser_details.purchaserType) {
              const hasPreferences = data.purchaser_details.preferences;
              console.log(`Agreement reviewed and purchaser type set, redirecting to start-agreement ${hasPreferences ? '(with preferences already set)' : ''}`);
              // Navigate to start-agreement - the AgreementWizard will handle showing the correct step
              navigate(`/${agreement_id}/start-agreement`);
            } else {
              console.log("Agreement already reviewed, redirecting to start-agreement");
              navigate(`/${agreement_id}/start-agreement`);
            }
          } else {
            console.log("Not redirecting: status =", data.status);
          }
        } else {
          console.log("Not redirecting due to back button navigation");
        }
      } catch (err) {
        console.error('Error fetching agreement:', err);
        setError('Failed to load agreement data');
      } finally {
        setLoading(false);
      }
    };

    fetchAgreement();
  }, [agreement_id, navigate, location.state]);

  // Handle continue button click
  const handleContinue = async () => {
    if (!agreement_id) return;
    
    // Only update status if it's specifically "sent"
    if (agreement && agreement.status === "sent") {
      try {
        setIsUpdatingStatus(true);
        await updateAgreementStatus(agreement_id, "reviewed");
      } catch (error) {
        console.error("Error updating agreement status:", error);
      } finally {
        setIsUpdatingStatus(false);
      }
    }
    
    // Navigate to the start-agreement route
    navigate(`/${agreement_id}/start-agreement`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row gap-6 mb-8">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {loading ? (
                "Loading..."
              ) : error ? (
                "Dear Guest,"
              ) : (
                `Dear ${agreement?.client?.name?.split(' ')?.[0] ||
                  agreement?.purchaser_details?.individualDetails?.[0]?.firstName ||
                  agreement?.purchaser_details?.individualDetails?.[0]?.name?.split(' ')?.[0] ||
                  "Guest"},`
              )}
            </h1>
            <div className="space-y-4 text-gray-700">
              <p>
                Purchasing property is one of the most impactful steps you can take towards building long-term wealth and achieving financial freedom. We understand how important it is to make confident, well-informed investment decisions, and that's where our team comes in!
              </p>
              <p>
                We've attached your proposal below for you to get started. If you have any questions or would like to discuss next steps, please don't hesitate to reach out. We're here to guide you at every stage of your property journey.
              </p>
            </div>
            <div className="mt-6">
              <div className="font-semibold text-lg text-gray-900">Ravi Sharma</div>
              <div className="text-gray-600">Founder & CEO</div>
            </div>
          </div>
          <div className="w-full sm:w-80 h-64 sm:h-96 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
            <img 
              src="/ravi.png"
              alt="Ravi Sharma"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>

        {/* Next Steps Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Next Steps
          </h2>
          <div className="space-y-4">
            {STEP_GUIDANCES.map((step, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => {
                    const newExpandedStep = expandedStep === index ? null : index;
                    setExpandedStep(newExpandedStep);
                    if (newExpandedStep !== null) {
                      setViewedSteps(prev => new Set([...prev, index]));
                    }
                  }}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 flex items-center gap-4 transition-all duration-200"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    expandedStep === index || viewedSteps.has(index) ? 'bg-primary-500 text-white' : 'bg-gray-300 text-gray-700'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <span className={`font-semibold ${
                      expandedStep === index ? 'text-primary-600' : 'text-gray-900'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-200 ${
                      expandedStep === index ? 'rotate-180 text-primary-600' : 'text-gray-400'
                    }`}
                  />
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedStep === index ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 py-4 bg-white border-t border-gray-200 transition-all duration-300">
                    {step.children}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ravi Intro Video Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Welcome to Search Property!
          </h2>
          <p className="text-gray-600 mb-6">
          We’re excited to have you on board! Here’s what’s ahead as you take the final step to get started on your investment property journey with us.
          </p>
          
          <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
            <iframe
              src="https://www.youtube.com/embed/HcpCrKD_PJc"
              className="w-full h-full"
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="Welcome Message from Ravi"
            ></iframe>
          </div>
        </div>

        {/* Investment Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your Investment
          </h2>
          <p className="text-orange-600 font-medium mb-6">
            {agreement && agreement.total_fee ? (
              <>
                A simple flat fee of ${agreement.total_fee.toLocaleString()} + GST, split over two payments
              </>
            ) : (
              "Please contact your Property Consultant for fee information"
            )}
          </p>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg">
              <div>
                <div className="font-semibold text-gray-900">Payment 1 - Initial Retainer</div>
                <div className="text-sm text-gray-600">Due today</div>
              </div>
              <div className="flex flex-col items-end">
                <div className="text-lg font-bold text-gray-900">
                  {agreement && agreement.total_retainer ? (
                    `$${agreement.total_retainer.toLocaleString()} + GST`
                  ) : (
                    "Contact Consultant"
                  )}
                </div>
                {agreement && agreement.total_retainer && (
                  <div className="text-sm text-gray-600">
                    {calculateWithGST(agreement.total_retainer)} inc. GST
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg">
              <div>
                <div className="font-semibold text-gray-900">Payment 2 - Success Fee</div>
                <div className="text-sm text-gray-600">Due after Settlement of your property</div>
              </div>
              <div className="flex flex-col items-end">
                <div className="text-lg font-bold text-gray-900">
                  {agreement && agreement.total_fee && agreement.total_retainer ? (
                    `$${(agreement.total_fee - agreement.total_retainer).toLocaleString()} + GST`
                  ) : (
                    "Contact Consultant"
                  )}
                </div>
                {agreement && agreement.total_fee && agreement.total_retainer && (
                  <div className="text-sm text-gray-600">
                    {calculateWithGST(agreement.total_fee - agreement.total_retainer)} inc. GST
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleContinue}
            disabled={isUpdatingStatus}
            className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-6 rounded-full font-semibold text-lg transition-colors duration-200 flex items-center justify-center"
          >
            {isUpdatingStatus ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Processing...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>

        {/* Thank You Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Thank You for Choosing Us!
          </h2>
          <p className="text-gray-700 mb-6">
            We look forward to working together and helping you achieve your goals. If you have any questions
            or need assistance, don't hesitate to reach out. Let's make great things happen!
          </p>
          <div className="flex items-center gap-4">
            <img
              src="/raviavatar.png"
              alt="Ravi Sharma"
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <div className="font-semibold text-gray-900">Ravi Sharma</div>
              <div className="text-gray-600">Principal & Lead Buyers Agent</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}